import { useCallback, useEffect, useState } from "react";

import { useSnackbar } from "notistack";

import { RecaptchaAction } from "../../../common/constants/recaptcha";
import { getTelemetryID } from "../../../common/helpers/stytch";
import { trpc } from "../../../common/helpers/trpc";
import { useRecaptchaRetry } from "../../../common/hooks/useRecaptchaRetry";

import { useTranslate } from "@/common/hooks/useTranslate";

const DEFAULT_RESEND_COOLDOWN = 120;

type UseResendCodeProps =
  | {
      email?: never;
    }
  | {
      feature: "login";
      email: string;
    };

export const useResendCode = ({ email }: UseResendCodeProps) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { executeTrpc } = useRecaptchaRetry();

  const { mutateAsync: reSendSmsOtp } = trpc.reSendSmsOtp.useMutation();

  const [resendCooldown, setResendCooldown] = useState(DEFAULT_RESEND_COOLDOWN);
  const [isLoadingSendSms, setIsLoadingSendSms] = useState(false);

  const resendButtonText = t("dashboard", "settings.twoFactorAuthBySmsModal.setup.code.resend");

  const isResendButtonDisabled = resendCooldown > 0 || isLoadingSendSms;
  const resendButtonCountdownText = resendCooldown > 0 ? `${resendButtonText} - ${resendCooldown}s` : resendButtonText;

  const resendCode = useCallback(async () => {
    setIsLoadingSendSms(true);
    try {
      const response = await executeTrpc(RecaptchaAction.SendSmsOtp, email || "", reSendSmsOtp, {
        telemetryId: (await getTelemetryID()) || undefined,
      });
      if (response.error) {
        enqueueSnackbar(t("dashboard", "common.gqlError"), { vibe: "danger" });
      } else {
        setResendCooldown(DEFAULT_RESEND_COOLDOWN);
        enqueueSnackbar(t("dashboard", "settings.twoFactorAuthBySmsModal.setup.code.resend.sent"), {
          vibe: "positive",
        });
      }
    } finally {
      setIsLoadingSendSms(false);
    }
  }, [email, enqueueSnackbar, executeTrpc, reSendSmsOtp, t]);

  // Counts the Resend lock down each second, from when the code window opens until the sent code expires
  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const tick = setTimeout(() => setResendCooldown((cooldown) => cooldown - 1), 1000);
    return () => clearTimeout(tick);
  }, [resendCooldown]);

  return {
    isResendButtonDisabled,
    resendButtonCountdownText,
    isLoadingSendSms,
    resendCode,
  };
};
